import { services, process } from "../data/services";
import PageHero from "../components/PageHero";
import Button from "../components/Button";
import { IconArrow } from "../components/icons";
import { BlueprintGrid, DimensionLine } from "../components/Glyphs";
import useReveal from "../components/useReveal";
import styles from "./Services.module.css";

export default function Services() {
  useReveal();
  return (
    <>
      <PageHero
        eyebrow="What We Do"
        code="A · 03"
        title="From back-of-napkin to certificate of occupancy."
        subtitle="UPSHOT runs three connected studios — residential, commercial, and full-scope design and planning. Every project is led by the principal architect, with a process that's deliberately small enough to stay personal."
      />

      <section className={styles.servicesSection}>
        <BlueprintGrid color="#1a1d20" opacity={0.06} step={32} />
        <div className={[styles.sectionInner, "reveal"].join(" ")}>
          <div className={styles.serviceList}>
            {services.map((s) => (
              <article
                key={s.id}
                className={[styles.serviceCard, "reveal"].join(" ")}
              >
                <div className={styles.serviceHead}>
                  <span className={styles.code}>{s.code}</span>
                  <h2 className={styles.serviceName}>{s.name}</h2>
                </div>
                <p className={styles.summary}>{s.summary}</p>
                <ul className={styles.itemList}>
                  {s.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className={styles.dim}>
        <DimensionLine color="#1a1d20" label="THE PROCESS · 04 PHASES" />
      </div>

      <section className={styles.process}>
        <div className={styles.processInner}>
          <div className={[styles.sectionHead, "reveal"].join(" ")}>
            <span className={styles.eyebrow}>How We Work</span>
            <h2 className={styles.h2}>
              Four phases. One conversation that runs the whole way.
            </h2>
            <p className={styles.lede}>
              The process is linear because buildings are built that way — but
              each phase folds back into the last. We sketch with the section,
              detail with the trades, and write specs we'd be willing to build
              from ourselves.
            </p>
          </div>

          <ol className={styles.steps}>
            {process.map((step, i) => (
              <li
                key={step.code}
                className={[styles.step, "reveal"].join(" ")}
                style={{ "--idx": i }}
              >
                <div className={styles.stepHead}>
                  <span className={styles.stepCode}>{step.code}</span>
                  <h3 className={styles.stepName}>{step.name}</h3>
                </div>
                <p className={styles.stepBody}>{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className={styles.engagement}>
        <div className={[styles.engagementInner, "reveal"].join(" ")}>
          <div className={styles.engCol}>
            <span className={styles.eyebrow}>Engagement</span>
            <h2 className={styles.h2}>How a project starts.</h2>
          </div>
          <ol className={styles.engList}>
            <li>
              <strong>Hello.</strong> A short call — what you're imagining, where,
              and roughly when. No commitment, no charge.
            </li>
            <li>
              <strong>Site visit.</strong> We walk the property together (or you
              walk it for us, with a phone). Notes, photos, slope, sun.
            </li>
            <li>
              <strong>Proposal.</strong> A written scope and fee. Phased so you
              can pause between schematic and CDs if anything changes.
            </li>
            <li>
              <strong>Design.</strong> Schematic → Design Development → Permit →
              Construction Documents. Drawings detailed enough that the builder
              doesn't have to guess.
            </li>
            <li>
              <strong>Construction.</strong> Site visits, RFI responses,
              submittal review. We don't disappear after permit.
            </li>
          </ol>
        </div>
      </section>

      <section className={styles.cta}>
        <div className={[styles.ctaInner, "reveal"].join(" ")}>
          <span className={styles.eyebrow}>Next</span>
          <h2 className={styles.h2}>Tell us about your project.</h2>
          <Button to="/contact" variant="primary" size="lg">
            Start a Project <IconArrow />
          </Button>
        </div>
      </section>
    </>
  );
}
